/**
 * AI Sidebar Chat - Core Logic
 *
 * Handles context building, AI response parsing, and change application
 * for the Cursor-like AI editing experience in the sidebar.
 */

import * as monaco from 'monaco-editor'

// ============================================================================
// Types
// ============================================================================

export interface ChatContext {
  body: string
  selectedText: string
  cursorLine: number
  isEmptyLine: boolean
  selectionStartLine: number
  selectionEndLine: number
}

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  threadMessageId?: number
  context?: ChatContext
  changes?: AiChange[]
  appliedChanges?: boolean
  rejectedChanges?: boolean
  streaming?: boolean
  timestamp: number
  images?: Array<{ url: string; filename: string; previewDataUrl?: string }>
  reasoningContent?: string
  firstResponseMs?: number
  stoppedByUser?: boolean
  model?: string
}

export interface AiChange {
  type: 'replace' | 'insert'
  startLine: number
  endLine: number
  content: string
  originalContent?: string
}

// ============================================================================
// Context Builder
// ============================================================================

/**
 * Build chat context from the current editor state
 */
export function buildChatContext(editor: monaco.editor.IStandaloneCodeEditor): ChatContext {
  const model = editor.getModel()
  if (!model) {
    return {
      body: '',
      selectedText: '',
      cursorLine: 1,
      isEmptyLine: true,
      selectionStartLine: 0,
      selectionEndLine: 0
    }
  }

  const body = model.getValue()
  const selection = editor.getSelection()
  const position = editor.getPosition()
  const cursorLine = position?.lineNumber || 1

  let selectedText = ''
  let selectionStartLine = 0
  let selectionEndLine = 0
  let isEmptyLine = false

  if (selection && !selection.isEmpty()) {
    selectedText = model.getValueInRange(selection)
    selectionStartLine = selection.startLineNumber
    selectionEndLine = selection.endLineNumber
  } else {
    // Check if current line is empty
    const lineContent = model.getLineContent(cursorLine)
    isEmptyLine = lineContent.trim().length === 0
  }

  return {
    body,
    selectedText,
    cursorLine,
    isEmptyLine,
    selectionStartLine,
    selectionEndLine
  }
}

/**
 * Build the user message with context info for display
 */
export function buildContextLabel(context: ChatContext): string {
  if (context.selectedText && context.selectionStartLine > 0) {
    if (context.selectionStartLine === context.selectionEndLine) {
      return `📎 Selected: Line ${context.selectionStartLine}`
    }
    return `📎 Selected: Lines ${context.selectionStartLine}-${context.selectionEndLine}`
  }
  if (context.isEmptyLine) {
    return `📍 Cursor: Line ${context.cursorLine} (empty)`
  }
  return `📍 Cursor: Line ${context.cursorLine}`
}

// ============================================================================
// Response Parser
// ============================================================================

/**
 * Parse AI response to extract changes and explanation text.
 * 
 * Robust parser handles ALL observed GPT-4o format variations:
 * - 2 or 3 backticks
 * - With or without space after backticks  
 * - With or without newline before <<<
 * - LINE or LINES keyword
 * - Single line number or range (N or N-M)
 *
 * Observed formats from GPT-4o:
 *   ```qeditor-replace\n<<<LINES:4-4>>>\n...\n```
 *   `` qeditor-insert<<<LINE:10>>>\n...\n``
 *   ``qeditor-replace<<<LINE:4>>>\n...\n``
 */

/**
 * Drop structurally-invalid QSyntax separators inside a generated fragment.
 * `===` / `---` are separators BETWEEN questions; a separator that touches the
 * metadata marker `~~~`, another separator, or the trailing boundary produces an
 * empty / orphan question and breaks the preview. We keep a separator only when it
 * sits between real content on both sides. A leading separator (real content after,
 * none before) is kept here and handled by the inline-diff insert logic, which knows
 * whether there is content to separate from.
 */
function normalizeSeparators(content: string): string {
  const lines = content.split('\n')
  const isSep = (l: string) => /^={3,}$/.test(l.trim()) || /^-{3,}$/.test(l.trim())
  const isMeta = (l: string) => /^~{3,}$/.test(l.trim())
  const out: string[] = []

  const prevReal = (): string | null => {
    for (let j = out.length - 1; j >= 0; j--) if (out[j].trim() !== '') return out[j]
    return null
  }
  const nextReal = (from: number): string | null => {
    for (let j = from; j < lines.length; j++) if (lines[j].trim() !== '') return lines[j]
    return null
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (isSep(line)) {
      const prev = prevReal()
      const nxt = nextReal(i + 1)
      // Drop unless flanked by real (non-separator, non-metadata) content on both sides
      if (!nxt || isMeta(nxt) || isSep(nxt) || (prev !== null && (isMeta(prev) || isSep(prev)))) {
        continue
      }
    }
    out.push(line)
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

/**
 * Normalize answer markers: QSyntax radio/checkbox use EMPTY brackets `( )` / `[ ]`
 * (the correct option is marked by points `$10`, not by a letter). Models sometimes
 * emit multiple-choice letters like `$10 (A) go`, which makes the parser choke
 * (it expects `)` right after `(`). Strip a single marker char inside the brackets
 * on answer lines. Multi-char brackets (textfield `$(ans|10)`, inline-select
 * `$(*[a,10],[b,0])`) are left untouched.
 */
function normalizeAnswerMarkers(content: string): string {
  return content.split('\n').map(line => {
    if (!/^\s*\$/.test(line)) return line
    return line
      .replace(/(\$\S*\s*)\(\s*[^\s)]\s*\)/, '$1( )')
      .replace(/(\$\S*\s*)\[\s*[^\s\]]\s*\]/, '$1[ ]')
  }).join('\n')
}

/**
 * Ensure a blank line between a question stem and its first `$` answer line.
 * The QSyntax parser uses a paragraph break (blank line) to reset `formLineStart`,
 * separating the question text (markdown) from the answer form. Without it the stem
 * is swallowed into the form and the preview renders raw text instead of inputs.
 */
function ensureAnswerBlankLine(content: string): string {
  const lines = content.split('\n')
  const isAnswer = (l: string) => /^\s*\$/.test(l) // standalone answer line (not inline $)
  const out: string[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (isAnswer(line) && out.length > 0) {
      const prev = out[out.length - 1]
      if (prev.trim() !== '' && !isAnswer(prev)) out.push('') // stem -> blank -> answers
    }
    out.push(line)
  }
  return out.join('\n')
}

/**
 * De-glue a structural marker that the model appended to a content line, e.g.
 * `$ ( )37~~~` or `+ Even/Odd===`. QSyntax matches `~~~` / `===` / `---` only when
 * they occupy a whole line (`/^~~~+$/m`, `/\n===+\n/`); glued to text they are
 * invisible to the parser, so the metadata split and the question split both fail
 * and the preview falls back to raw text. Move the marker onto its own line.
 *
 * `~~~` (metadata) is always split off. `===` / `---` (question separators) are NOT
 * split off answer lines (`$...`) so the textarea marker `$=====` stays intact.
 */
function deglueStructuralMarkers(content: string): string {
  const out: string[] = []
  for (const line of content.split('\n')) {
    const meta = line.match(/^(.*\S)(~{3,})\s*$/)
    if (meta && !/^\s*~/.test(line)) {
      out.push(meta[1].replace(/\s+$/, ''), '', meta[2])
      continue
    }
    const sep = line.match(/^(.*\S)(={3,}|-{3,})\s*$/)
    if (sep && !/^\s*\$/.test(line)) {
      out.push(sep[1].replace(/\s+$/, ''), '', sep[2])
      continue
    }
    out.push(line)
  }
  return out.join('\n')
}

/**
 * Ensure a space between an EMPTY answer bracket and its label: `$10 ( )8` -> `$10 ( ) 8`.
 * The tokenizer expects whitespace after `)` / `]` before the label; without it a
 * radio/checkbox line is not recognised and renders as raw text. Only empty brackets
 * are touched, so textfield `$(ans|10)` / inline-select `$(*[a,10])` are left alone.
 */
function ensureAnswerMarkerSpace(content: string): string {
  return content.split('\n').map(line => {
    if (!/^\s*\$/.test(line)) return line
    return line.replace(/(\(\s*\)|\[\s*\])(?=\S)/, '$1 ')
  }).join('\n')
}

/**
 * Ensure a blank line before a standalone structural marker (`~~~` / `===` / `---`).
 * Defensive: keeps the options block visually and structurally separated from the
 * metadata / next-question boundary even when the model omits the blank line.
 */
function ensureMarkerBlankLine(content: string): string {
  const lines = content.split('\n')
  const out: string[] = []
  for (const line of lines) {
    if (/^(~{3,}|={3,}|-{3,})$/.test(line.trim()) && out.length > 0 && out[out.length - 1].trim() !== '') {
      out.push('')
    }
    out.push(line)
  }
  return out.join('\n')
}

/**
 * Full QSyntax normalization pipeline for AI-generated content, shared by the
 * sidebar chat and the Cmd+K inline edit so both paths produce parser-valid QSyntax.
 */
export function normalizeQSyntaxContent(content: string): string {
  return ensureMarkerBlankLine(
    ensureAnswerBlankLine(
      ensureAnswerMarkerSpace(
        normalizeAnswerMarkers(
          normalizeSeparators(
            deglueStructuralMarkers(content)
          )
        )
      )
    )
  )
}

export function parseAiResponse(response: string, cursorLine?: number, templateMode: boolean = false): { explanation: string; changes: AiChange[] } {
  const changes: AiChange[] = []

  // Line-based scanner. The free-text regex approach broke on multi-block replies
  // when the model fenced sloppily (two blocks in one fence, or a missing closing
  // fence): it captured only the first block and dropped the rest. We instead scan
  // line by line for `<<<LINE(S):n(-m)>>>` headers and collect each block's content
  // up to the next header / qeditor-token / closing fence, tolerant of fencing style.
  const headerRe = /<<<\s*(LINES?)\s*:\s*(\d+)\s*(?:-\s*(\d+))?\s*>>>/i
  const typeRe = /(?:`{2,3}\s*)?qeditor-(replace|insert)/i
  const fenceOnlyRe = /^\s*`{2,3}\s*(?:qeditor-(?:replace|insert))?\s*$/i

  const lines = response.split('\n')
  const explanationLines: string[] = []
  let pendingType: 'replace' | 'insert' | null = null
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const typeMatch = line.match(typeRe)
    const headerMatch = line.match(headerRe)

    // A `qeditor-replace/insert` token (often on its own fence line) sets the type
    // for the header that follows.
    if (typeMatch && !headerMatch) {
      pendingType = typeMatch[1].toLowerCase() as 'replace' | 'insert'
      i++
      continue
    }

    if (headerMatch) {
      const startLine = parseInt(headerMatch[2], 10)
      const endLine = headerMatch[3] ? parseInt(headerMatch[3], 10) : startLine
      let type: 'replace' | 'insert'
      if (typeMatch) type = typeMatch[1].toLowerCase() as 'replace' | 'insert'
      else if (pendingType) type = pendingType
      else type = (headerMatch[3] || headerMatch[1].toUpperCase() === 'LINES') ? 'replace' : 'insert'
      pendingType = null

      // Collect content until the next block boundary.
      const contentLines: string[] = []
      i++
      while (i < lines.length) {
        const l = lines[i]
        if (headerRe.test(l)) break          // next block header
        if (typeRe.test(l)) break            // next qeditor-token (fenced or bare)
        if (fenceOnlyRe.test(l)) { i++; break } // closing fence: consume and stop
        contentLines.push(l)
        i++
      }
      // Strip trailing whitespace per line: QSyntax separators are matched exactly
      // (`/\n===+\n/`, `/^~~~+$/m`), so a stray trailing space on `=== ` / `~~~ `
      // breaks group/metadata parsing and the preview fails to render.
      const rawContent = contentLines
        .map(l => l.replace(/\s+$/, ''))
        .join('\n')
        .replace(/^\n+/, '')
        .replace(/\n+$/, '')
      const content = normalizeQSyntaxContent(rawContent)
      if (content.length > 0) {
        changes.push({ type, startLine, endLine: type === 'insert' ? startLine : endLine, content })
      }
      continue
    }

    // Plain explanation line (drop pure fence lines)
    if (!fenceOnlyRe.test(line)) explanationLines.push(line)
    i++
  }

  let explanation = explanationLines.join('\n')

  // Template mode fallback ONLY: if no qeditor blocks found, treat response as raw content
  // This is specifically for exam generation templates, NOT for general chat
  if (templateMode && changes.length === 0 && response.trim().length > 0) {
    let rawContent = response

    // Strip plain code block markers if present (```...```)
    const plainBlockRegex = /^```[a-z]*\n?([\s\S]*?)\n?```$/gm
    const plainMatch = plainBlockRegex.exec(rawContent)
    if (plainMatch) {
      rawContent = plainMatch[1]
    }

    // Strip leftover qeditor headers: INSERT<<<LINE:N>>>, -insert<<<LINE:N>>>, qeditor-insert<<<LINE:N>>> etc.
    rawContent = rawContent.replace(/^(?:qeditor-)?(?:-)?(?:INSERT|REPLACE|insert|replace)\s*\n?\s*<<<(?:LINES?):[\d-]+>>>\s*\n?/gim, '')

    rawContent = rawContent.replace(/\r\n/g, '\n').trim()

    if (rawContent.length > 0) {
      // Insert at cursor position if available, otherwise line 1
      const insertLine = (cursorLine && cursorLine > 0) ? cursorLine : 1
      changes.push({
        type: 'insert',
        startLine: insertLine,
        endLine: insertLine,
        content: rawContent
      })
      explanation = ''
    }
  }

  // Clean up extra whitespace in explanation
  explanation = explanation.replace(/\n{3,}/g, '\n\n').trim()

  return { explanation, changes }
}

// ============================================================================
// Change Application
// ============================================================================

/**
 * Apply a single change to the editor
 */
export function applyChange(
  editor: monaco.editor.IStandaloneCodeEditor,
  change: AiChange
): boolean {
  const model = editor.getModel()
  if (!model) return false

  try {
    if (change.type === 'replace') {
      const endLine = Math.min(change.endLine, model.getLineCount())
      const endColumn = model.getLineMaxColumn(endLine)
      editor.executeEdits('ai-chat', [{
        range: new monaco.Range(change.startLine, 1, endLine, endColumn),
        text: change.content,
        forceMoveMarkers: true
      }])
    } else if (change.type === 'insert') {
      const lineCount = model.getLineCount()
      const targetLine = Math.min(change.startLine, lineCount)
      const lineContent = model.getLineContent(targetLine)

      if (lineContent.trim().length === 0) {
        // Empty line: replace it with new content
        const endColumn = model.getLineMaxColumn(targetLine)
        editor.executeEdits('ai-chat', [{
          range: new monaco.Range(targetLine, 1, targetLine, endColumn),
          text: change.content,
          forceMoveMarkers: true
        }])
      } else {
        // Non-empty line: insert after it
        const endColumn = model.getLineMaxColumn(targetLine)
        editor.executeEdits('ai-chat', [{
          range: new monaco.Range(targetLine, endColumn, targetLine, endColumn),
          text: '\n' + change.content,
          forceMoveMarkers: true
        }])
      }
    }
    return true
  } catch (error) {
    console.error('Failed to apply AI change:', error)
    return false
  }
}

/**
 * Apply all changes from an AI response.
 * Changes are applied bottom-first to preserve line numbers.
 */
export function applyAllChanges(
  editor: monaco.editor.IStandaloneCodeEditor,
  changes: AiChange[],
  onContentChange?: (content: string) => void
): boolean {
  if (changes.length === 0) return false

  // Sort bottom-first to preserve line numbers
  const sorted = [...changes].sort((a, b) => b.startLine - a.startLine)

  let allSucceeded = true
  for (const change of sorted) {
    if (!applyChange(editor, change)) {
      allSucceeded = false
    }
  }

  // Trigger content change callback
  if (onContentChange) {
    const model = editor.getModel()
    if (model) {
      onContentChange(model.getValue())
    }
  }

  return allSucceeded
}

// ============================================================================
// Diff Helpers
// ============================================================================

/**
 * Get the original content for a change range (for diff display)
 */
export function getOriginalContent(
  body: string,
  change: AiChange
): string {
  const lines = body.split('\n')
  if (change.type === 'replace') {
    return lines.slice(change.startLine - 1, change.endLine).join('\n')
  }
  // For insert, original is empty
  return ''
}

/**
 * Generate a unique message ID
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}
